import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../i18n";
import { useFmt } from "../i18n/fmt";
import { useAuthStore } from "../store/authStore";
import { useBreakpoint } from "../hooks/useBreakpoint";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

type Analytics = {
  changes_month: number; total_relevant: number;
  portfolio_month: number; competitor_month: number;
  trend: { date: string; count: number }[];
  by_type: { type: string; count: number }[];
  by_area: { area: string; count: number }[];
  // Propio frente a competencia (feedback de Carlos, 20 oct 2026): barras agrupadas
  by_block?: { grupo: number; nombre: string; own: number; competitor: number }[];
  by_area_split?: { area: string; atc: string | null; own: number; competitor: number }[];
  top_labs: { lab: string; count: number }[];
  by_source: { source: string; count: number }[];
};

const OWN = "#0891b2";
const COMP = "#f59e0b";

const card: React.CSSProperties = {
  background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18,
  backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
  boxShadow: "var(--clr-shadow-sm)", padding: "1.25rem",
};
const cardTitle: React.CSSProperties = { margin: "0 0 14px", fontSize: "0.85rem", fontWeight: 700, color: "var(--clr-text)" };
const tooltipStyle = { background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 8, fontSize: "0.78rem", color: "var(--clr-text)" };

function ArrowUpRightIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 17 17 7M9 7h8v8" />
    </svg>
  );
}

function KPI({ label, value, sub, color, onClick }: { label: string; value: number; sub: string; color?: string; onClick?: () => void }) {
  const [hov, setHov] = useState(false);
  const active = hov && !!onClick;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        ...card,
        position: "relative",
        cursor: onClick ? "pointer" : "default",
        boxShadow: active ? "var(--clr-shadow)" : "var(--clr-shadow-sm)",
        transform: active ? "translateY(-2px)" : "none",
        borderColor: active ? "var(--clr-org-primary)" : "var(--clr-border)",
        transition: "transform 0.2s cubic-bezier(.25,.6,.3,1), box-shadow 0.2s, border-color 0.2s",
      }}
    >
      {/* Afordancia de "clicable": flecha ↗ arriba-derecha (como en superadmin) */}
      {onClick && (
        <span style={{ position: "absolute", top: 14, right: 14, display: "flex", color: active ? "var(--clr-org-primary)" : "var(--clr-text4)", transition: "color 0.2s" }}>
          <ArrowUpRightIcon />
        </span>
      )}
      <div style={{ fontSize: "2rem", fontWeight: 800, lineHeight: 1, color: color || "var(--clr-text)" }}>{value}</div>
      <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--clr-text2)", marginTop: 8 }}>{label}</div>
      <div style={{ fontSize: "0.7rem", color: "var(--clr-text4)", marginTop: 2 }}>{sub}</div>
    </div>
  );
}

/** Ranking horizontal simple (barra proporcional), reutilizado por fuentes y labs. */
function BarList({ rows, G, isMobile }: { rows: { label: string; count: number }[]; G: string; isMobile: boolean }) {
  const max = Math.max(...rows.map(r => r.count), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {rows.map(r => (
        <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: isMobile ? 110 : 190, fontSize: "0.8rem", color: "var(--clr-text2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }}>{r.label}</div>
          <div style={{ flex: 1, background: "var(--clr-border2)", borderRadius: 999, height: 10, overflow: "hidden" }}>
            <div style={{ width: `${(r.count / max) * 100}%`, height: "100%", background: G, borderRadius: 999 }} />
          </div>
          <div style={{ width: 32, textAlign: "right", fontSize: "0.8rem", fontWeight: 700 }}>{r.count}</div>
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsDashboardPage() {
  const t = useT();
  const fmt = useFmt();
  const navigate = useNavigate();
  const G = useAuthStore(s => s.user?.primary_color) || "#0891b2";
  const { isMobile } = useBreakpoint();
  const [data, setData] = useState<Analytics | null>(null);
  const [error, setError] = useState("");

  // Un click en un KPI abre la lista de Cambios ya filtrada (30d salvo el total).
  const monthFrom = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10) + "T00:00:00";
  const goChanges = (params: string, labelKey: string) =>
    navigate(`/changes?${params}label=${encodeURIComponent(t(labelKey))}`);

  useEffect(() => {
    api.get<Analytics>("/dashboard/analytics").then(setData).catch(e => setError(e.message));
  }, []);

  if (error) return <div style={{ padding: "2rem", color: "var(--clr-text4)" }}>{t("analytics.loadError")}</div>;
  if (!data) return <div style={{ padding: "2rem", color: "var(--clr-text4)" }}>{t("analytics.loading")}</div>;

  const hasData = data.total_relevant > 0;
  // Cada barra abre los eventos que la componen: mismo periodo (90 días), bloque o ATC y lado.
  const from90 = new Date(Date.now() - 90 * 864e5).toISOString().slice(0, 10) + "T00:00:00";
  const sideParam = (side: "own" | "competitor") => side === "own" ? "is_portfolio=true" : "is_competitor=true";
  const sideLabel = (side: "own" | "competitor") => t(side === "own" ? "analytics.seriesOwn" : "analytics.seriesCompetitor");
  const blocks = (data.by_block ?? []).map(b => ({ ...b, label: t(`hitos.groupShort${b.grupo}`) }));
  const areas = (data.by_area_split ?? []).slice(0, 8);
  const goBlock = (g: number, side: "own" | "competitor") => navigate(`/changes?from_date=${from90}&hito_group=${g}&${sideParam(side)}&label=${
    encodeURIComponent(t("analytics.navBlock", { block: t(`hitos.group${g}`), side: sideLabel(side) }))}`);
  const goArea = (a: { area: string; atc: string | null }, side: "own" | "competitor") => a.atc && navigate(`/changes?from_date=${from90}&atc=${a.atc}&${sideParam(side)}&label=${
    encodeURIComponent(t("analytics.navArea", { area: a.area, side: sideLabel(side) }))}`);
  const legend = (
    <div style={{ display: "flex", gap: 14, fontSize: "0.72rem", color: "var(--clr-text3)", margin: "-6px 0 8px", flexWrap: "wrap" }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: 3, background: OWN }} />{t("analytics.seriesOwn")}</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: 3, background: COMP }} />{t("analytics.seriesCompetitor")}</span>
      <span style={{ color: "var(--clr-text4)" }}>· {t("analytics.barHint")}</span>
    </div>
  );

  return (
    <div style={{ padding: isMobile ? "1rem" : "1.75rem 2rem", color: "var(--clr-text)", fontVariantNumeric: "tabular-nums" }}>
      <div style={{ marginBottom: "1.5rem" }}>
        <h1 style={{ margin: 0, fontSize: "1.3rem", fontWeight: 700 }}>{t("analytics.pageTitle")}</h1>
        <p style={{ margin: "4px 0 0", fontSize: "0.82rem", color: "var(--clr-text4)" }}>{t("analytics.subtitle")}</p>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: isMobile ? 10 : 16, marginBottom: 20 }}>
        <KPI label={t("analytics.kpiMonth")} value={data.changes_month} sub={t("analytics.kpiMonthSub")} color={G}
          onClick={() => goChanges(`from_date=${monthFrom}&`, "analytics.navMonth")} />
        <KPI label={t("analytics.kpiPortfolio")} value={data.portfolio_month} sub={t("analytics.kpiMonthSub")} color="#0891b2"
          onClick={() => goChanges(`from_date=${monthFrom}&is_portfolio=true&`, "analytics.navPortfolio")} />
        <KPI label={t("analytics.kpiCompetitor")} value={data.competitor_month} sub={t("analytics.kpiMonthSub")} color="#f59e0b"
          onClick={() => goChanges(`from_date=${monthFrom}&is_competitor=true&`, "analytics.navCompetitor")} />
        <KPI label={t("analytics.kpiTotal")} value={data.total_relevant} sub={t("analytics.kpiTotalSub")}
          onClick={() => goChanges("", "analytics.navTotal")} />
      </div>

      {!hasData ? (
        <div style={{ ...card, textAlign: "center", color: "var(--clr-text4)", padding: "3rem" }}>{t("analytics.empty")}</div>
      ) : (
        <>
          {/* Tendencia 30 días */}
          <div style={{ ...card, marginBottom: 16 }}>
            <h3 style={cardTitle}>{t("analytics.trendTitle")}</h3>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <AreaChart data={data.trend} margin={{ top: 6, right: 12, bottom: 0, left: -18 }}>
                  <defs>
                    <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={G} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={G} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="var(--clr-border2)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={d => fmt.date(new Date(d + "T12:00:00"), { day: "2-digit", month: "2-digit" })} tick={{ fontSize: 10, fill: "var(--clr-text4)" }} minTickGap={24} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--clr-text4)" }} />
                  <Tooltip contentStyle={tooltipStyle} labelFormatter={d => fmt.date(new Date(d + "T12:00:00"), { day: "numeric", month: "short" })} formatter={(v: number) => [v, t("analytics.changesLabel")]} />
                  <Area type="monotone" dataKey="count" stroke={G} strokeWidth={2} fill="url(#trendGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Por bloque del catálogo + por área, propio frente a competencia (Carlos, 20 oct 2026) */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div style={card}>
              <h3 style={cardTitle}>{t("analytics.byTypeTitle")}</h3>
              {legend}
              <div style={{ width: "100%", height: Math.max(220, blocks.length * 34 + 30) }}>
                <ResponsiveContainer>
                  <BarChart data={blocks} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 6 }} barGap={2}>
                    <CartesianGrid stroke="var(--clr-border2)" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "var(--clr-text4)" }} />
                    <YAxis type="category" dataKey="label" tick={{ fontSize: 10, fill: "var(--clr-text3)" }} width={isMobile ? 100 : 150} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "var(--clr-hover)" }}
                      formatter={(v: number, k: string) => [v, k === "own" ? t("analytics.seriesOwn") : t("analytics.seriesCompetitor")]} />
                    <Bar dataKey="own" fill={OWN} radius={[0, 5, 5, 0]} cursor="pointer"
                      onClick={(d: { payload?: { grupo: number } }) => d.payload && goBlock(d.payload.grupo, "own")} />
                    <Bar dataKey="competitor" fill={COMP} radius={[0, 5, 5, 0]} cursor="pointer"
                      onClick={(d: { payload?: { grupo: number } }) => d.payload && goBlock(d.payload.grupo, "competitor")} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div style={card}>
              <h3 style={cardTitle}>{t("analytics.byAreaTitle")}</h3>
              {legend}
              <div style={{ width: "100%", height: Math.max(220, areas.length * 34 + 30) }}>
                <ResponsiveContainer>
                  <BarChart data={areas} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 6 }} barGap={2}>
                    <CartesianGrid stroke="var(--clr-border2)" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "var(--clr-text4)" }} />
                    <YAxis type="category" dataKey="area" tick={{ fontSize: 10, fill: "var(--clr-text3)" }} width={isMobile ? 100 : 150} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "var(--clr-hover)" }}
                      formatter={(v: number, k: string) => [v, k === "own" ? t("analytics.seriesOwn") : t("analytics.seriesCompetitor")]} />
                    <Bar dataKey="own" fill={OWN} radius={[0, 5, 5, 0]} cursor="pointer"
                      onClick={(d: { payload?: { area: string; atc: string | null } }) => d.payload && goArea(d.payload, "own")} />
                    <Bar dataKey="competitor" fill={COMP} radius={[0, 5, 5, 0]} cursor="pointer"
                      onClick={(d: { payload?: { area: string; atc: string | null } }) => d.payload && goArea(d.payload, "competitor")} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Por fuente + Top laboratorios */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
            <div style={card}>
              <h3 style={cardTitle}>{t("analytics.bySourceTitle")}</h3>
              {data.by_source.length === 0
                ? <div style={{ color: "var(--clr-text4)", fontSize: "0.82rem", padding: "1rem 0" }}>{t("analytics.noSource")}</div>
                : <BarList rows={data.by_source.map(s => ({ label: s.source, count: s.count }))} G={G} isMobile={isMobile} />}
            </div>
            <div style={card}>
              <h3 style={cardTitle}>{t("analytics.topLabsTitle")}</h3>
              {data.top_labs.length === 0
                ? <div style={{ color: "var(--clr-text4)", fontSize: "0.82rem", padding: "1rem 0" }}>{t("analytics.noLabs")}</div>
                : <BarList rows={data.top_labs.map(l => ({ label: l.lab, count: l.count }))} G={G} isMobile={isMobile} />}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
